/**
 * @linkhr/hr-domain/payroll-run — Zod-at-the-boundary schema for payroll runs.
 *
 * The backend's `RunView` schema has `status` typed as plain `string` — the
 * actual enum values (DRAFT | COMPUTING | COMPUTED | FINALIZING | FINALIZED | PAID)
 * are documented in the contract (CC-37) but not expressed as an OpenAPI enum.
 * We defend with a Zod schema at the hook boundary.
 *
 * Behaviour:
 *   1. Each run goes through `safeRunParse(row)`. Returns either
 *      `{ ok: true, data }` or `{ ok: false, fallback, issues }` — does NOT throw.
 *   2. On failure, log a console.warn (+ Sentry breadcrumb via the payslip-line
 *      pattern) so production telemetry surfaces drift. The run STILL renders
 *      with safe fallbacks (status="DRAFT").
 *   3. `status` uses `.catch("DRAFT")` so unknown enum values coerce gracefully.
 *
 * Per project conventions Testing Philosophy this is MUST-test tier (data-transformation
 * at a correctness boundary — wrong status causes wrong action buttons to render).
 */

import { z } from "zod";

export const RUN_STATUS_VALUES = [
  "DRAFT",
  "COMPUTING",
  "COMPUTED",
  "FINALIZING",
  "FINALIZED",
  "PAID",
  // Terminal, and reached without anyone pressing anything: cancelling a leave
  // voids the run that was going to pay it, at any state up to and including
  // finalized. Missing from this list it coerced to DRAFT, which is the one
  // status that offers Compute — so a voided run invited an admin to restart
  // paying a leave that no longer exists.
  "VOIDED",
] as const;

export type RunStatus = (typeof RUN_STATUS_VALUES)[number];

/**
 * Distinct unknown status values already warned about this session. The status
 * `.catch` fires on EVERY parse (the run-detail poll re-parses every ~5s), so
 * without this a single drifted value would spam the console; we surface each
 * distinct value once. Module-level so it persists across parses.
 */
const warnedStatusValues = new Set<string>();

/**
 * Mirrors `components["schemas"]["RunView"]` from the generated OpenAPI schema.
 * Uses `.catch("DRAFT")` on `status` to handle future enum additions gracefully.
 *
 * Named `PayrollRunView` (not `RunView`) to avoid collisions with other View types
 * in the generated schema — per fe-059 mitigation strategy.
 */
export const PayrollRunView = z.object({
  id: z.string().optional(),
  tenantId: z.string().optional(),
  // Backend returns `null` for unset FKs (no branch on tenant-wide runs, no
  // origin on REGULAR runs, no `paidBy` until paid, etc.). `.optional()`
  // alone allows `undefined` but rejects `null`, which made every row fail
  // Zod parse and rendered the runs board as DRAFT-fallback rows. Widen.
  branchId: z.string().nullable().optional(),
  branchScope: z.string().optional(),
  year: z.number().int().min(2020).max(2100).optional(),
  month: z.number().int().min(1).max(12).optional(),
  runType: z.string().optional(), // "REGULAR" | "ADJUSTMENT" | "SETTLEMENT" | "LEAVE_PREPAY"
  // `.catch` keeps the run rendering with a safe DRAFT default when the backend
  // sends an unknown status (forward-compat guard, unchanged). But a silent
  // coerce hides real drift, so log a warning when it ACTUALLY fires — the
  // fallback value is identical, this only adds visibility (no crash).
  status: z.enum(RUN_STATUS_VALUES).catch((ctx) => {
    const received = ctx.input;
    const key = typeof received === "string" ? received : JSON.stringify(received);
    if (!warnedStatusValues.has(key)) {
      warnedStatusValues.add(key);
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn("[payroll-run-status-coerced]", { received });
      }
    }
    return "DRAFT";
  }),
  originRunId: z.string().nullable().optional(), // present on ADJUSTMENT runs
  createdAt: z.string().optional(),
  finalizedAt: z.string().nullable().optional(),
  finalizedBy: z.string().nullable().optional(),
  paidAt: z.string().nullable().optional(),
  paidBy: z.string().nullable().optional(),
  lastErrorSummary: z.string().nullable().optional(),
  /**
   * The statutory deadline for paying this run, snapshotted when it was
   * created. Informational only — the server never blocks a late payment, so
   * nothing here may gate an action. Absent means no deadline applies.
   */
  dueBy: z.string().nullable().optional(),
  /**
   * The leave request this run pays ahead of, on a LEAVE_PREPAY run. Null on
   * every other run type. Payroll owns the link in both directions — a leave
   * screen finds its run by filtering the runs list on this id, because the
   * leave request itself carries no run reference.
   */
  leaveRequestId: z.string().nullable().optional(),
  /**
   * The person a SETTLEMENT run pays off. Null on every other run type — a
   * LEAVE_PREPAY run names its leave request instead, and a REGULAR run is
   * about everyone. Lets a settlement run link back to the profile it came
   * from, which was previously a one-way trip.
   */
  employeeId: z.string().nullable().optional(),
  /**
   * Why the last compute refused, structured. `lastErrorSummary` is the
   * server's prose rendering of this same data; this is what lets a screen
   * tell an Art. 77 refusal apart from an unexpected failure and act on it.
   * Unknown codes still carry their args, so copy can degrade without losing
   * the detail.
   */
  lastError: z
    .object({
      errorCode: z.string(),
      messageKey: z.string().nullable().optional(),
      args: z.record(z.string(), z.string()).default({}),
      legalBasis: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  employeeCount: z.number().int().optional(),
  grossTotal: z.number().optional(),
  deductionsTotal: z.number().optional(),
  netTransferTotal: z.number().optional(),
  // Correction (ADJUSTMENT) runs: what the run asks employees to pay back.
  // Zero on every other run and before compute; optional for older rows.
  recoveryTotal: z.number().nullable().optional(),
  // Regular runs: active employees left out because they are not on payroll on
  // the month's last day (an owner not insured through the company). Null on
  // other run types and on the list read.
  offPayrollExcludedCount: z.number().int().nullable().optional(),
  // CC-299 — employer-side social-security total for the run (employer cost,
  // separate from netTransferTotal / deductionsTotal). Optional for pre-CC-299
  // cached rows; the run-detail tile falls back to 0 when absent.
  employerSscTotal: z.number().optional(),
  // Run-level twins of the per-line split: the end-of-service awards in this
  // run, and the earnings total that reconciles to the net transfer. Same rule
  // as the line — `grossTotal` stays wage gross. Absent on pre-split rows.
  eosTotal: z.number().optional(),
  payableEarningsTotal: z.number().optional(),
  // The currency the run computed in, frozen at open. Optional for rows cached
  // before the backend served it; every consumer falls back to JOD.
  currencyCode: z.string().optional(),
  // Changes whenever the run's status or calculation changes. "Calculate
  // again" sends it back so a run someone else changed meanwhile is refused.
  // Optional so a server that doesn't send it yet still parses; without it
  // the action is simply not offered.
  version: z.number().int().optional(),
  // Who took each step, in the reader's language; null until that step (or
  // for a run the system created). Absent from servers that don't send them.
  // Who a settlement or advance leave run is for, and their last day. Null for a
  // reader without full payroll access. The schema strips what it doesn't list.
  employeeName: z.string().nullable().optional(),
  employeeNameAr: z.string().nullable().optional(),
  employeeCode: z.string().nullable().optional(),
  employmentEndDate: z.string().nullable().optional(),
  createdByName: z.string().nullable().optional(),
  finalizedByName: z.string().nullable().optional(),
  paidByName: z.string().nullable().optional(),
  // The run's recorded steps, oldest first; only the single-run read fills it.
  // `event` stays a string so a step added later still renders, and a history
  // that doesn't parse is dropped rather than failing the whole run.
  history: z
    .array(
      z.object({
        event: z.string(),
        at: z.string(),
        actorName: z.string().nullable().optional(),
        reason: z.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional()
    .catch(null),
});

export type PayrollRunView = z.infer<typeof PayrollRunView>;

/**
 * Conservative fallback used when the entire row fails Zod parse.
 * Renders with DRAFT status so action buttons are disabled (safe default).
 */
const FALLBACK: PayrollRunView = { status: "DRAFT" };

export type SafeRunParseResult =
  | { ok: true; data: PayrollRunView }
  | { ok: false; fallback: PayrollRunView; issues: z.ZodIssue[] };

/**
 * Sentry global accessor — mirrors payslip-line.ts pattern.
 */
type SentryShim = {
  captureMessage?: (msg: string, ctx?: { level?: string; extra?: unknown }) => void;
};

function captureRunDrift(issues: z.ZodIssue[], raw: unknown): void {
  if (typeof window === "undefined") return;
  const sentry = (window as unknown as { Sentry?: SentryShim }).Sentry;
  if (sentry?.captureMessage === undefined) return;
  sentry.captureMessage("payroll-run-shape-drift", {
    level: "warning",
    extra: { issues, raw },
  });
}

/**
 * Validates a single payroll run at the hook boundary. Never throws.
 *
 * Usage at the hook layer:
 *
 *   const runs = (page.content ?? []).map((row) => {
 *     const r = safeRunParse(row);
 *     return r.ok ? r.data : r.fallback;
 *   });
 */
export function safeRunParse(row: unknown): SafeRunParseResult {
  const result = PayrollRunView.safeParse(row);
  if (result.success) return { ok: true, data: result.data };

  captureRunDrift(result.error.issues, row);
  if (typeof console !== "undefined" && typeof console.warn === "function") {
    console.warn("[payroll-run-shape-drift]", { issues: result.error.issues, row });
  }

  return { ok: false, fallback: { ...FALLBACK }, issues: result.error.issues };
}
